import { COLORS } from '../../constants';
import type { Brick, Dev } from '../../types/domain';
import { absenceTypeFromBrick, absenceTypeLabel, isAbsenceBrick } from '../absences';
import { isPhaseEntry, isProjectEntry, phaseIdFromEntry, projectIdFromEntry } from './identifiers';
import type { ClientAndPhase, Clients } from './types';

// ── Nouveau modèle : clients > phases > briques ──────────────────────────
export function clientColor(clients: Clients, clientId: string): string {
  const client = (clients || []).find((c) => c.id === clientId);
  if (client?.color) return client.color;
  const i = (clients || []).findIndex((c) => c.id === clientId);
  return COLORS[i >= 0 ? i % COLORS.length : 0];
}

export function devsByIdMap(devs: Dev[] | null | undefined): Record<string, Dev> {
  return Object.fromEntries((devs || []).map((d) => [d.id, d]));
}

export function getBrickById(clients: Clients, id: string): Brick | null {
  for (const c of clients || [])
    for (const ph of c.phases || []) {
      const b = (ph.briques || []).find((x) => x.id === id);
      if (b) return b;
    }
  return null;
}

export function getClientByBrick(clients: Clients, bid: string): ClientAndPhase | null {
  for (const c of clients || [])
    for (const ph of c.phases || []) {
      if ((ph.briques || []).some((b) => b.id === bid)) return { client: c, phase: ph };
    }
  return null;
}

export function getPhaseById(clients: Clients, phaseId: string): ClientAndPhase | null {
  for (const c of clients || []) {
    const ph = (c.phases || []).find((x) => x.id === phaseId);
    if (ph) return { client: c, phase: ph };
  }
  return null;
}

export function entryLabel(clients: Clients, briqueId: string): string {
  if (isAbsenceBrick(briqueId)) return absenceTypeLabel(absenceTypeFromBrick(briqueId));
  if (isPhaseEntry(briqueId)) {
    const res = getPhaseById(clients, phaseIdFromEntry(briqueId));
    return res ? `${res.client.nom} — ${res.phase.nom} (général)` : 'Phase (général)';
  }
  if (isProjectEntry(briqueId)) {
    const clientId = projectIdFromEntry(briqueId);
    const c = (clients || []).find((x) => x.id === clientId);
    return c ? `${c.nom} (global)` : 'Projet global';
  }
  const res = getClientByBrick(clients, briqueId);
  if (res) {
    const b = (res.phase.briques || []).find((x) => x.id === briqueId);
    return b ? `${res.client.nom} — ${b.nom}` : res.client.nom;
  }
  if (briqueId === '__general__') return 'Autre';
  return '(brique supprimée)';
}

// Compat export : liste plate projets (client = projet, toutes phases fusionnées)
export function clientsToFlatProjects(
  clients: Clients,
): { id: string; nom: string; briques: Brick[]; phaseIds: string[] }[] {
  return (clients || []).map((c) => ({
    id: c.id,
    nom: c.nom,
    briques: (c.phases || []).flatMap((ph) => ph.briques || []),
    phaseIds: (c.phases || []).map((ph) => ph.id),
  }));
}
